import { RootState, store } from 'duck/store'
import { TemporalEntityId } from 'entities-next/common'
import { isEmpty, isNil, negate, pickBy } from 'lodash'
import { connect } from 'react-redux'
import { DateTrustCode } from 'scenes/Dashboard/data/common/types'
import {
  getOrganizationLinkedinUrl,
  getOrganizationLogoUrl,
  getOrganizationWebsiteUrl,
} from 'scenes/Dashboard/data/digitalPresences/helpers'
import { DigitalPresenceType } from 'scenes/Dashboard/data/digitalPresences/types'
import { displayLocation } from 'scenes/Dashboard/data/locations/helpers'
import { updateOrganization } from 'scenes/Dashboard/data/organizations/actions'
import {
  displayIndustry,
  getOrganizationEvents,
  getOrganizationHeadquarters,
  getOrganizationIndustries,
  getOrganizationOperatingLocations,
  getOrganizationPerformanceIndicators,
} from 'scenes/Dashboard/data/organizations/helpers'
import { getOrganizationPositions } from 'scenes/Dashboard/data/organizations/selectors'
import {
  LocationType,
  Organization,
  OrganizationData,
  OrganizationIndustry,
  OrganizationIndustryData,
  OrganizationLocation,
  OrganizationLocationData,
  OrganizationSource,
} from 'scenes/Dashboard/data/organizations/types'
import { getFullNameFromPerson, getRoleFromPosition } from 'scenes/Dashboard/data/people/selectors'
import { getFormFormattedDate } from 'utils'

import { AutoCompleteOption, AutoCompleteStageOption } from '../../../Inputs/Select/AutoComplete/AutoComplete.model'
import { submitForm } from '../../duck/actions'
import { getOrganizationDetailsModalState } from '../../duck/selectors'
import { getAttachmentsFromOrganization, getSignalAssessmentsFromOrganization } from '../../utils/getters'
import {
  parseFormValueToOrganizationEvent,
  parseOrganizationEventToFormValue,
} from './components/OrganizationEvents/OrganizationEventEdit.models'
import {
  parseFormValueToPerformanceIndicator,
  parsePerformanceIndicatorToFormValue,
} from './components/PerformanceIndicators/PerformanceIndicatorsEditList.models'
import { OrganizationFormValues } from './OrganizationDetailsEdit.models'
import OrganizationDetailsEdit, { OrganizationDetailsEditProps } from './OrganizationDetailsEdit.presentational'

function mapStateToProps(state: RootState, { organization }: ContainerProps): StateProps {
  const { isSubmitting } = getOrganizationDetailsModalState(state)
  return {
    submitting: isSubmitting,
    initialValues: mapOrganizationDataToFormValues(organization || undefined),
    onSubmit: (values: OrganizationFormValues) => {
      const updatedOrg = {
        ...mapFormValuesToOrganizationData(values, organization!.id),
        id: organization!.id,
      }
      store.dispatch(
        updateOrganization.request({
          organization: updatedOrg,
        })
      )
    },
    onCancel: () => store.dispatch(submitForm.failure()),
  }
}

type StateProps = Pick<OrganizationDetailsEditProps, 'submitting' | 'initialValues' | 'onSubmit' | 'onCancel'>

type ContainerProps = Pick<OrganizationDetailsEditProps, 'organization'>

export function parseLocationRelationToFormValue({ location }: OrganizationLocation) {
  return {
    value: { id: location.id },
    display: { label: displayLocation(location) },
  }
}

export function parseIndustryRelationToFormValue({ industry }: OrganizationIndustry) {
  return {
    value: { id: industry.id },
    display: { label: displayIndustry(industry) },
    isValid: industry.isInOurTaxonomy,
  }
}

export function mapOrganizationDataToFormValues(organization?: OrganizationData): OrganizationFormValues {
  if (!organization) {
    return {
      organizationId: TemporalEntityId.new,
      industryValues: [],
      headquarters: [],
      operatingLocations: [],
      attachmentsValues: [],
      foundersFullTimeValue: false,
      experienceInIndustryValue: false,
      serialEntrepreneursValue: false,
      signalAssessmentsValues: [],
      performanceIndicatorsValues: [],
      organizationEventsValues: [],
      releasedToNetworkManagerValue: false,
      communicateRoundClosedValue: false,
      founders: [],
      balancedFoundingTeamValue: false,
      foundersHaveOtherRelevantExperienceValue: false,
      techFounderWithRelevantDegreeValue: false,
      processedByOpsValue: false,
    }
  }

  const {
    id: organizationId,
    blurb,
    types,
    source,
    insights,
    description,
    foundersFullTime,
    foundersFullTimeExplanation,
    experienceInIndustry,
    experienceInIndustryExplanation,
    serialEntrepreneurs,
    serialEntrepreneursExplanation,
    otherTractionMetrics,
    status,
    reportedLikelihoodFundraising,
    referrers,
    currentRoundAnnouncedOn,
    rejectedInvestors,
    founderNotes,
    investorsInCapTable,
    totalFundingRaised,
    lastRoundType,
    lastRoundClosedOn,
    releasedToNetworkManager,
    communicateRoundClosed,
    communicateRoundClosedDescription,
    foundedOn,
    balancedFoundingTeam,
    foundersHaveOtherRelevantExperience,
    techFounderWithRelevantDegree,
    processedByOps,
  } = organization
  const headquarters = getOrganizationHeadquarters(organization)
  const operatingLocations = getOrganizationOperatingLocations(organization)
  const industries = getOrganizationIndustries(organization)
  const performanceIndicators = getOrganizationPerformanceIndicators(organization)
  const organizationEvents = getOrganizationEvents(organization)
  const founders = getOrganizationPositions(organization)
    .filter((position) => !isNil(position.person))
    .map((position) => `${getFullNameFromPerson(position.person!)} (${getRoleFromPosition(position)}) `)

  return {
    organizationId,
    nameValue: organization.name,
    domainValue: organization.domain,
    websiteUrlValue: getOrganizationWebsiteUrl(organization) || '',
    linkedinUrlValue: getOrganizationLinkedinUrl(organization) || '',
    logoUrlValue: getOrganizationLogoUrl(organization),
    blurbValue: blurb || '',
    sourceValue: source
      ? { value: source, label: source }
      : { value: OrganizationSource.outbound, label: OrganizationSource.outbound },
    typesValue: types ? types.map((type) => ({ value: type, label: type })) : undefined,
    headquarters: headquarters.map(parseLocationRelationToFormValue),
    operatingLocations: operatingLocations.map(parseLocationRelationToFormValue),
    industryValues: industries.map(parseIndustryRelationToFormValue),
    statusValue: status ? { value: status, label: status } : undefined,
    likelihoodValue: reportedLikelihoodFundraising ? getFormFormattedDate(reportedLikelihoodFundraising as Date) : '',
    currentRoundAnnouncedOn: currentRoundAnnouncedOn ? getFormFormattedDate(currentRoundAnnouncedOn as Date) : '',
    insightsValue: insights || '',
    descriptionValue: description || '',
    foundersFullTimeValue: foundersFullTime || false,
    foundersFullTimeExplanationValue: foundersFullTimeExplanation || '',
    experienceInIndustryExplanationValue: experienceInIndustryExplanation || '',
    experienceInIndustryValue: experienceInIndustry || false,
    serialEntrepreneursExplanationValue: serialEntrepreneursExplanation || '',
    serialEntrepreneursValue: serialEntrepreneurs || false,
    otherTractionMetricsValue: otherTractionMetrics || '',
    referrersValue: referrers ? referrers.map((referral) => referral.value) : [],
    attachmentsValues: getAttachmentsFromOrganization({ organization }),
    signalAssessmentsValues: getSignalAssessmentsFromOrganization({ organization }),
    performanceIndicatorsValues: performanceIndicators.map(parsePerformanceIndicatorToFormValue),
    organizationEventsValues: organizationEvents.map(parseOrganizationEventToFormValue),
    rejectedInvestorsValue: rejectedInvestors || '',
    investorsInCapTableValue: investorsInCapTable || '',
    lastRoundTypeValue: lastRoundType
      ? ({
          value: { name: lastRoundType },
          display: { label: lastRoundType },
        } as AutoCompleteOption)
      : ('' as const),
    totalFundingRaisedValue: totalFundingRaised,
    lastRoundClosedOnValue: lastRoundClosedOn ? getFormFormattedDate(lastRoundClosedOn as Date) : '',
    founderNotesValue: founderNotes || '',
    releasedToNetworkManagerValue: releasedToNetworkManager || false,
    communicateRoundClosedValue: communicateRoundClosed || false,
    communicateRoundClosedExplanationValue: communicateRoundClosedDescription || '',
    foundedOnValue: foundedOn ? getFormFormattedDate(foundedOn as Date) : '',
    founders: founders || [],
    balancedFoundingTeamValue: balancedFoundingTeam,
    foundersHaveOtherRelevantExperienceValue: foundersHaveOtherRelevantExperience,
    techFounderWithRelevantDegreeValue: techFounderWithRelevantDegree,
    processedByOpsValue: processedByOps || false,
  }
}

function createIndustryRelations(
  organizationId: Organization['id'],
  industryValues: AutoCompleteOption[]
): OrganizationIndustryData[] {
  return industryValues.map((option) => ({
    organizationId,
    industry: option.value,
  }))
}

function createLocationRelations(
  organizationId: Organization['id'],
  { headquarters, operatingLocations }: { headquarters: AutoCompleteOption[]; operatingLocations: AutoCompleteOption[] }
): OrganizationLocationData[] {
  const headquartersValues = headquarters.map((option) => ({
    organizationId,
    type: LocationType.headquarters,
    location: option.value,
  }))
  const operatingLocationsValues = operatingLocations.map((option) => ({
    organizationId,
    type: LocationType.operating,
    location: option.value,
  }))
  return [...headquartersValues, ...operatingLocationsValues]
}

function mapFormValuesToOrganizationData(
  {
    attachmentsValues,
    websiteUrlValue,
    logoUrlValue,
    linkedinUrlValue,
    nameValue,
    domainValue,
    sourceValue,
    statusValue,
    likelihoodValue,
    blurbValue,
    insightsValue,
    descriptionValue,
    foundersFullTimeValue,
    foundersFullTimeExplanationValue,
    experienceInIndustryValue,
    experienceInIndustryExplanationValue,
    serialEntrepreneursValue,
    serialEntrepreneursExplanationValue,
    otherTractionMetricsValue,
    currentRoundAnnouncedOn,
    signalAssessmentsValues,
    performanceIndicatorsValues,
    organizationEventsValues,
    rejectedInvestorsValue,
    investorsInCapTableValue,
    lastRoundTypeValue,
    totalFundingRaisedValue,
    lastRoundClosedOnValue,
    founderNotesValue,
    releasedToNetworkManagerValue,
    communicateRoundClosedValue,
    communicateRoundClosedExplanationValue,
    foundedOnValue,
    balancedFoundingTeamValue,
    foundersHaveOtherRelevantExperienceValue,
    techFounderWithRelevantDegreeValue,
    processedByOpsValue,
    ...other
  }: OrganizationFormValues,
  organizationId: number
): OrganizationData {
  const digitalPresence = [...attachmentsValues]

  const headquarters = other.headquarters || []
  const operatingLocations = other.operatingLocations || []
  const industryValues = other.industryValues || []
  const typesValue = other.typesValue || []
  const referrers = other.referrersValue || []

  if (websiteUrlValue) {
    digitalPresence.push({ type: DigitalPresenceType.websiteUrl, value: websiteUrlValue })
  }

  if (logoUrlValue) {
    digitalPresence.push({ type: DigitalPresenceType.logoUrl, value: logoUrlValue })
  }

  if (linkedinUrlValue) {
    digitalPresence.push({ type: DigitalPresenceType.linkedinUrl, value: linkedinUrlValue })
  }

  const organizationData = {
    name: nameValue!,
    domain: domainValue,
    types: typesValue.map((type) => type.value),
    source: sourceValue ? sourceValue.value : OrganizationSource.outbound,
    locationsRelations: createLocationRelations(organizationId, { headquarters, operatingLocations }),
    industriesRelations: createIndustryRelations(organizationId, industryValues),
    status: statusValue ? statusValue.value : '',
    reportedLikelihoodFundraising: likelihoodValue || '',
    blurb: blurbValue,
    insights: insightsValue,
    digitalPresence: !isEmpty(digitalPresence) ? digitalPresence : undefined,
    description: descriptionValue,
    foundersFullTime: foundersFullTimeValue,
    foundersFullTimeExplanation: foundersFullTimeExplanationValue,
    experienceInIndustry: experienceInIndustryValue,
    experienceInIndustryExplanation: experienceInIndustryExplanationValue,
    serialEntrepreneurs: serialEntrepreneursValue,
    serialEntrepreneursExplanation: serialEntrepreneursExplanationValue,
    otherTractionMetrics: otherTractionMetricsValue,
    currentRoundAnnouncedOn: currentRoundAnnouncedOn || '',
    currentRoundAnnouncedOnTrustCode: currentRoundAnnouncedOn ? DateTrustCode.yearDayMonth : DateTrustCode.null,
    referrers: referrers.map((referral) => ({ value: referral })),
    rejectedInvestors: rejectedInvestorsValue,
    signalAssessments: signalAssessmentsValues.map((signal) => ({ ...signal, organizationId })),
    performanceIndicators: performanceIndicatorsValues.map(parseFormValueToPerformanceIndicator(organizationId)),
    events: organizationEventsValues.map(parseFormValueToOrganizationEvent(organizationId)),
    investorsInCapTable: investorsInCapTableValue,
    lastRoundType: !isEmpty(lastRoundTypeValue)
      ? ((lastRoundTypeValue as AutoCompleteOption).value as AutoCompleteStageOption).name
      : undefined,
    totalFundingRaised: totalFundingRaisedValue,
    lastRoundClosedOn: lastRoundClosedOnValue,
    founderNotes: founderNotesValue,
    releasedToNetworkManager: releasedToNetworkManagerValue,
    communicateRoundClosed: communicateRoundClosedValue,
    communicateRoundClosedDescription: communicateRoundClosedValue ? communicateRoundClosedExplanationValue : '',
    foundedOn: foundedOnValue,
    balancedFoundingTeam: balancedFoundingTeamValue,
    foundersHaveOtherRelevantExperience: foundersHaveOtherRelevantExperienceValue,
    techFounderWithRelevantDegree: techFounderWithRelevantDegreeValue,
    processedByOps: processedByOpsValue,
  }

  const nonEmptyOrganizationData = pickBy(organizationData, negate(isNil)) as OrganizationData
  return nonEmptyOrganizationData
}

export default connect(mapStateToProps)(OrganizationDetailsEdit)
